import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { typography } from '../../theme/typography';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface ToggleProps {
  value: boolean;
  onValueChange: (value: boolean) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  style?: any;
  containerStyle?: any;
}

export const Toggle: React.FC<ToggleProps> = ({
  value,
  onValueChange,
  label,
  description,
  disabled = false,
  size = 'md',
  style,
  containerStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const handlePress = () => {
    if (!disabled) {
      onValueChange(!value);
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return {
          switchWidth: 40,
          switchHeight: 24,
          thumbSize: 20,
          thumbTranslate: 16,
        };
      case 'lg':
        return {
          switchWidth: 56,
          switchHeight: 32,
          thumbSize: 28,
          thumbTranslate: 24,
        };
      default:
        return {
          switchWidth: 48,
          switchHeight: 28,
          thumbSize: 24,
          thumbTranslate: 20,
        };
    }
  };

  const sizeStyles = getSizeStyles();

  return (
    <TouchableOpacity
      style={[styles.container, containerStyle, disabled && styles.disabled]}
      onPress={handlePress}
      activeOpacity={0.7}
      disabled={disabled}
    >
      <View style={styles.content}>
        {label && (
          <View style={styles.labelContainer}>
            <Text style={[styles.label, disabled && styles.labelDisabled]}>
              {label}
            </Text>
            {description && (
              <Text style={[styles.description, disabled && styles.descriptionDisabled]}>
                {description}
              </Text>
            )}
          </View>
        )}
        <View
          style={[
            styles.switch,
            {
              width: sizeStyles.switchWidth,
              height: sizeStyles.switchHeight,
            },
            value && styles.switchActive,
            disabled && styles.switchDisabled,
            style,
          ]}
        >
          <View
            style={[
              styles.thumb,
              {
                width: sizeStyles.thumbSize,
                height: sizeStyles.thumbSize,
                borderRadius: sizeStyles.thumbSize / 2,
              },
              value && {
                transform: [{ translateX: sizeStyles.thumbTranslate }],
              },
            ]}
          />
        </View>
      </View>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    marginBottom: spacing.md,
  },
  content: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  labelContainer: {
    flex: 1,
    marginRight: spacing.md,
  },
  label: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  labelDisabled: {
    color: colors.text.secondary,
  },
  description: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    lineHeight: 20,
  },
  descriptionDisabled: {
    color: colors.text.secondary,
    opacity: 0.6,
  },
  switch: {
    borderRadius: 14,
    backgroundColor: colors.border,
    justifyContent: 'center',
    paddingHorizontal: 2,
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 1,
    }),
  },
  switchActive: {
    backgroundColor: colors.primary,
  },
  switchDisabled: {
    opacity: 0.5,
  },
  thumb: {
    backgroundColor: colors.background.surface,
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.2,
      shadowRadius: 2,
      elevation: 2,
    }),
  },
  disabled: {
    opacity: 0.6,
  },
});

